import { Form, Row, Col } from 'react-bootstrap';
import { useController, UseFormReturn } from 'react-hook-form';
import { DateTime } from 'luxon';
import { useState, useEffect } from 'react';

/**
 * Internal state used by this DateTime picker field
 */
export interface DateTimeInternalState {
  date?: string;
  time?: string;
}

/**
 * Parse the default value date
 */
export function parseDefaultValue(value: string): DateTimeInternalState {
  const { isValid } = DateTime.fromISO(value);

  if (isValid) {
    // Remove any timezone offset
    // This field assumes the date given is in the correct zone (i.e. we should
    // have converted it to the correct zone first), as the user will expect it
    // to be in the correct timezone (by not thinking about zones at all). We
    // therefore need to remove any timezone offset string (or Z for UTC).
    const valueWithoutTimezone = value.replace(/([+-][\d]{2}:[\d]{2}|Z)$/, '');

    // DateTime now assumes it is in the users local timezone, which means it
    // won't try to change it
    const dateTime = DateTime.fromISO(valueWithoutTimezone);

    // Return the formatted dates
    return {
      date: dateTime.toFormat('yyyy-LL-dd'),
      time: dateTime.toFormat('HH:mm'),
    };
  }

  // If invalid return undefined
  return {
    date: '',
    time: '',
  };
}

/**
 * Create the update value (for the form state)
 *
 * Creates an update value as an ISO style time, without a timezone offset.
 *
 * Returns undefined if the date is invalid (e.g. if a user puts an incorrectly
 * formatted time in on an old browser that doesn't have a time-specific input
 * field). This way we can keep editing until it is correct and then it will
 * update and feed through again.
 */
export function createUpdateValue(
  dateTime: DateTimeInternalState,
): string | undefined {
  const newDate = DateTime.fromISO(`${dateTime.date}T${dateTime.time}:00`);
  return newDate.isValid
    ? newDate.toFormat("yyyy-LL-dd'T'HH:mm:ss.SSS")
    : undefined;
}

/**
 * Valid time pattern
 *
 * Safari desktop (and potentially other very old browsers) doesn't support
 * input="time" yet, so it defaults to showing a text field. This regex is used
 * to check if the text input is in the correct format. Note that input="date"
 * is supported by most browsers. Currently the portion of browsers that need
 * this is about 3%.
 */
const validTimePattern = /([01]?[0-9]{1}|2[0-3]{1}):[0-5]{1}[0-9]{1}/;

/**
 * Get a valid time from the DateTime state, or the default
 *
 * Used to set a default as soon as the date is set (so the whole form is either
 * null or valid).
 */
export function validTimeOrDefault(
  dateTime: Pick<DateTimeInternalState, 'time'>,
) {
  return dateTime.time?.match(validTimePattern) ? dateTime.time : '00:00';
}

/**
 * Get a valid date from the DateTime state, or the default
 *
 * Used to set a default as soon as the time is set (so the whole form is either
 * null or valid).
 */
export function validDateOrDefault(
  dateTime: Pick<DateTimeInternalState, 'date'>,
) {
  return dateTime.date || DateTime.now().startOf('year').toISODate();
}

/**
 * DateTime Picker
 *
 * Creates a date and time picker that are combined together to look like one
 * Bootstrap field.
 *
 * __Timezone Note__
 *
 * This field presents any date by assuming it is in the correct zone already
 * (and ignores any timezone offset). For example if you set the default as
 * "2020-01-01T00:00:00+04:00", it will simply show the time part as midnight
 * regardless of the current users local timezone.
 *
 * If you have a datetime that is in UTC, make sure to convert it to the
 * appropriate timezone (e.g. trip timezone for group start dates) before
 * setting the default value, so that the host can see and edit assuming that it
 * is in the correct timezone for the trip. Then in the `onSubmit` handler you
 * will need to convert back to UTC. *
 */
export default function DateTimePicker({
  control,
  name,
  required = false,
  ariaLabel = '',
  readOnly = false,
}: DateTimePickerProps) {
  const {
    field: { onChange, onBlur, value },
  } = useController({ name, control });

  // Setup the internal field state with initial values, if set
  const [dateTime, setDateTime] = useState<DateTimeInternalState>(() =>
    parseDefaultValue(value),
  );

  // Update the whole react-hook-form state, whenever the internal state changes
  useEffect(() => {
    const newDate = createUpdateValue(dateTime);
    if (newDate) {
      onChange(newDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateTime]); // Do not add onChange here, as it appears to lead to a race condition

  return (
    <Row style={{ maxWidth: 350 }}>
      <Col xs={6} className="pe-0">
        <Form.Control
          aria-label={`${ariaLabel} date`}
          onBlur={onBlur}
          required={required}
          type="date"
          style={{
            borderRight: 0,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            paddingRight: 0,
          }}
          onChange={(e) => {
            setDateTime({
              date: e.target.value,
              time: validTimeOrDefault(dateTime),
            });
          }}
          value={dateTime.date}
          readOnly={readOnly}
        />
      </Col>
      <Col xs={6} className="ps-0">
        <Form.Control
          aria-label={`${ariaLabel} time`}
          onBlur={onBlur}
          required={required}
          style={{
            borderLeft: 0,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            paddingLeft: 5,
          }}
          type="time"
          // Check the pattern as Safari Desktop gives a plain text field
          pattern={validTimePattern.toString()}
          value={dateTime.time}
          onChange={(e) => {
            setDateTime({
              date: validDateOrDefault(dateTime),
              time: e.target.value,
            });
          }}
          readOnly={readOnly}
        />
      </Col>
    </Row>
  );
}

export interface DateTimePickerProps {
  /**
   * Aria Label
   *
   * E.g. "Group start" which will result in labels "Group start date" and
   * "Group start time" for screen readers.
   */
  ariaLabel?: string;
  control: UseFormReturn<any>['control'];
  required?: boolean;
  name: string;
  readOnly?: boolean;
}
